import type { HTMLAttributes, ReactNode, JSX } from 'react';
import { Button, type ButtonVariant } from './Button';

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  title?: string;
  icon?: ReactNode;
  onClose?: () => void;
  action?: {
    label: string;
    onClick: () => void;
    variant?: ButtonVariant;
  };
}

const getVariantClasses = (variant: AlertVariant): string => {
  switch (variant) {
    case 'info':
      return 'bg-info-light border-info text-info-dark';
    case 'success':
      return 'bg-success-light border-success text-success-dark';
    case 'warning':
      return 'bg-warning-light border-warning text-warning-dark';
    case 'error':
      return 'bg-error-light border-error text-error-dark';
  }
};

export function Alert({
  variant = 'info',
  title,
  icon,
  onClose,
  action,
  className = '',
  children,
  ...props
}: AlertProps): JSX.Element {
  const baseStyles = 'rounded-md border p-4';

  const combinedClassName = [
    baseStyles,
    getVariantClasses(variant),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={combinedClassName} role="alert" {...props}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {icon && <div className="flex-shrink-0 mt-0.5">{icon}</div>}
          <div className="flex-1 min-w-0">
            {title && <h3 className="font-medium mb-1">{title}</h3>}
            <div className="text-sm">{children}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {action && (
            <Button
              onClick={action.onClick}
              variant={action.variant || 'primary'}
              size="sm"
              className="w-full md:w-auto whitespace-nowrap"
            >
              {action.label}
            </Button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex rounded-md p-1.5 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
              aria-label="Close"
            >
              <svg
                className="h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
